/** Periodic detail baked once, replacing the pond shader's repeated procedural noise evaluations per fragment. */
export const riverPatternPixels = (size = 256): Uint8Array => {
    const out = new Uint8Array(size * size * 4)
    const hash = (x: number, y: number) => {
        let n = Math.imul(x, 374761393) ^ Math.imul(y, 668265263)
        n = Math.imul(n ^ n >>> 13, 1274126177)
        return ((n ^ n >>> 16) >>> 0) / 4294967295
    }
    const noise = (x: number, y: number, sx: number, sy: number) => {
        const xx = x / sx, yy = y / sy, ix = Math.floor(xx), iy = Math.floor(yy)
        const fx = xx - ix, fy = yy - iy, u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy)
        const at = (a: number, b: number) => hash(a % (size / sx), b % (size / sy))
        const a = at(ix, iy) * (1 - u) + at(ix + 1, iy) * u
        const b = at(ix, iy + 1) * (1 - u) + at(ix + 1, iy + 1) * u
        return a * (1 - v) + b * v
    }
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
        const i = (y * size + x) * 4
        out[i] = Math.round((noise(x, y, 8, 2) * .65 + noise(x, y, 4, 2) * .35) * 255)
        out[i + 1] = Math.round(noise(x, y, 4, 16) * 255)
        out[i + 2] = Math.round(noise(x, y, 16, 16) * 255)
        out[i + 3] = 255
    }
    return out
}

export const riverFragment = (maxWaders: number): string => `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
varying vec2 vUvs;
uniform sampler2D uField, uFlow, uRefl, uBank, uPattern, uCoords, uBankRefl;
uniform vec2 uSize, uOrigin;
uniform float uTime, uFlowPx, uReflAmt, uDay, uProjected;
uniform vec3 uR0, uR1, uR2, uR3, uR4, uDeeper, uMid, uLight, uFoam, uSpark, uReflTint;
uniform vec4 uWaders[${maxWaders}];
uniform float uWaderCount, uReflecting;
uniform vec4 uReflRect;
float ringDist(vec2 p, vec2 c, float r) {
    vec2 d = (p - c) / vec2(1.0, 0.42);
    float e = length(d);
    return abs(e - r) / max(length(vec2(d.x, d.y / 0.42) / max(e, .001)), .001);
}
void main() {
    vec2 local = floor(vUvs * uSize), uv = (local + .5) / uSize;
    vec2 p = uOrigin + local + .5;
    vec4 F = texture2D(uField, uv);
    vec4 bank = vec4(0.0);
    if (uProjected > .5) bank = texture2D(uBank, uv);
    float depth = F.r * 63.75;
    if (depth < .5) { gl_FragColor = bank; return; }
    vec4 FL = texture2D(uFlow, uv);
    vec2 flow = (FL.rg * 255.0 - 128.0) / 127.0;
    float speed = length(flow), t = uTime;
    // River-aligned coordinates make the main pattern travel downstream through each bend.
    // The shared travel rate never resets or crossfades, so the current remains easy to read.
    vec4 C = texture2D(uCoords, uv);
    float along = C.r * 65280.0 + C.g * 255.0 - 32768.0;
    float across = C.b * 65280.0 + C.a * 255.0 - 32768.0;
    vec2 stream = vec2(along - t * 18.0, across);
    vec3 detail = texture2D(uPattern, stream / 256.0).rgb;
    // Offset the breakup for each narrow streamlet; long parallel ribbons cannot span a pool.
    float lane = floor(across / 3.0);
    float dash = fract((stream.x + sin(lane * 12.71) * 47.0) / 17.0);
    float broken = smoothstep(.08, .2, dash) * (1.0 - smoothstep(.52, .72, dash));
    detail.r = mix(.5, detail.r, broken);
    // Sheltered pockets retain a faint local shimmer; the downstream ribbons remain dominant.
    float sheltered = (1.0 - smoothstep(.12, .28, speed)) * smoothstep(8.0, 18.0, depth);
    if (sheltered > .01) {
        float eddy = texture2D(uPattern, (p - flow * t * 12.0) / 256.0).r;
        detail.r = mix(detail.r, eddy, sheltered * .18);
    }
    // Faster, sparse riffles sit over the slower primary current without obscuring it.
    float fast = texture2D(uPattern, vec2(along - t * 32.0, across) / 256.0).g;
    float band = clamp((depth - 1.0) / 20.0, 0.0, 1.0) * 4.0 + (detail.b - .5) * .6;
    vec3 col = band < .55 ? uR0 : band < 1.5 ? uR1 : band < 2.5 ? uR2 : band < 3.4 ? uR3 : uR4;
    if (FL.b + (detail.b - .5) * .18 > .5) col = uDeeper;
    if (detail.r > .86 - speed * .025) col = uLight;
    else if (detail.r > .78 - speed * .025) col = uMid;
    float riffle = smoothstep(.5, .9, speed) * (1.0 - FL.b * .65);
    if (fast > .86 && speed > .5 && depth > 3.0) col = mix(uLight, uFoam, riffle * .75);
    // Sparse, broken turbulence at the drop: no translucent wash over the water.
    if (F.a > .08) {
        vec3 fixedDetail = texture2D(uPattern, vec2(along * 1.8, across * 1.6) / 256.0).rgb;
        float pocket = smoothstep(.42, .72, fixedDetail.b);
        float foam = F.a * pocket * smoothstep(3.0, 10.0, depth);
        // Keep the bed visible between small, crisp foam marks. The footprint stays on the drop.
        if (foam > .25 && detail.r > .68) col = uLight;
        if (foam > .4 && detail.r > .81 && fast > .52) col = uFoam;
    }
    float rim = (.8 + detail.g * 2.5) * mix(.25, 1.0, F.b);
    if (depth < rim) col = detail.r > .65 ? uFoam : uLight;
    else if (depth < rim + 1.0 + F.b) col = uMid;
    if (uWaderCount > .5) {
        for (int i = 0; i < ${maxWaders}; i++) {
            vec4 wd = uWaders[i];
            if (wd.w < .5) continue;
            float d = ringDist(p, wd.xy, wd.z);
            if (d < .75) col = p.y > wd.y ? uFoam : uLight;
            else if (d < 1.6 && p.y > wd.y) col = uMid;
            float rr = fract(t * .5 + wd.w * .37);
            if (ringDist(p, wd.xy, wd.z + 3.0 + rr * 16.0) < .6 && detail.g > rr * .8) col = uMid;
        }
    }
    float wob = floor(sin(p.y * .85 + t * 2.3) * 1.25 + .5);
    vec4 R = texture2D(uBankRefl, uv);
    if (uReflecting > .5) {
        vec2 reflectUV = (local + .5 + vec2(wob, 0.0) - uReflRect.xy) / uReflRect.zw;
        if (reflectUV.x >= 0.0 && reflectUV.y >= 0.0 && reflectUV.x < 1.0 && reflectUV.y < 1.0) {
            vec4 actor = texture2D(uRefl, reflectUV);
            R += actor * (1.0 - R.a);
        }
    }
    if (R.a > .01) col = mix(col, (R.rgb / R.a) * uReflTint, uReflAmt * R.a);
    col *= 1.0 - F.g * .2;
    if (uDay > .5 && detail.r > .95 && sin(t * 2.6 + p.x * .8) > .94) col = uSpark;
    gl_FragColor = mix(vec4(col, 1.0), bank, bank.a);
}`
